import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { API_ROUTES } from '../constants/apiRoutes';
import { QUERY_KEYS } from '../constants';
import { FINPSP_DEPOSIT_CHANNELS, FINPSP_PAYMENT_STATUS, FINPSP_PAYMENT_QUERY_KEYS } from '../constants/finpsp';
import {
  getFinpspCheckoutUrl,
  getFinpspStatusKey,
  isFinpspSubmissionUncertain,
  isFinpspTerminal,
} from '../helpers/payment/finpsp';
import { useModal } from '../hooks/useDialog';
import useLocalization from '../hooks/useLocalization';
import { useInvalidateQuery } from '../hooks/useInvalidateQuery';
import { useExtendedSnackbar } from '../hooks/useExtendedSnackbar';
import { usePaymentResponseHandler } from '../hooks/usePaymentResponseHandler';
import { finpspInitiationSchema } from '../schemas/finpsp';
import { PAYMENT_TRANSACTION_TYPE, type PaymentTransactionType } from '../types/enums';
import type { FinpspDepositRequest, FinpspInitiationResponse, FinpspWithdrawalRequest } from '../types/finpsp';
import { postData } from '../utils/api';
import { getFinpspPaymentKey } from '../queries/finpsp';

const useFinpspCreation = <T extends FinpspDepositRequest | FinpspWithdrawalRequest>(
  route: string,
  operation: PaymentTransactionType,
  failureMessage: string
) => {
  const invalidate = useInvalidateQuery();
  const { showErrorMessage, enqueueSnackbar } = useExtendedSnackbar();
  const { closeModal } = useModal();
  const { t } = useLocalization();
  const { handlePaymentError } = usePaymentResponseHandler();
  const queryClient = useQueryClient();
  const { marketplaceId, userId } = useAuth();

  return useMutation<FinpspInitiationResponse, Error, T>({
    mutationFn: async (body) => finpspInitiationSchema.parse(await postData(route, body, 'wallet')),
    retry: false,
    onSuccess: (response, body) => {
      if ('authResults' in response) {
        const { limitName, limitValue } = response.authResults[0];

        showErrorMessage(limitName, limitValue === undefined ? undefined : { limitValue });
        return;
      }

      void invalidate(FINPSP_PAYMENT_QUERY_KEYS);

      if (
        operation === PAYMENT_TRANSACTION_TYPE.DEPOSIT &&
        FINPSP_DEPOSIT_CHANNELS[body.type].checkout === 'instructions'
      ) {
        queryClient.setQueryData(getFinpspPaymentKey(marketplaceId, userId, response.transactionId), response);
        return;
      }

      if (operation === PAYMENT_TRANSACTION_TYPE.DEPOSIT && response.status === FINPSP_PAYMENT_STATUS.APPROVED) {
        void invalidate([QUERY_KEYS.firstDepositBonus, QUERY_KEYS.welcomeBonusAvailability]);
      }

      if (isFinpspTerminal(response)) {
        const message = t(getFinpspStatusKey(response));

        if (response.status === FINPSP_PAYMENT_STATUS.APPROVED) {
          enqueueSnackbar(message, { variant: 'success' });
        } else {
          enqueueSnackbar(message, { variant: 'error' });
        }

        closeModal();
        return;
      }

      if (operation === PAYMENT_TRANSACTION_TYPE.WITHDRAW) {
        enqueueSnackbar(t('withdrawRequestSubmitted'), { variant: 'success' });
        closeModal();
        return;
      }

      const channel = FINPSP_DEPOSIT_CHANNELS[body.type];
      const checkoutUrl = getFinpspCheckoutUrl(response.redirect_url);

      if (channel.checkout === 'redirect' && checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      enqueueSnackbar(t('finpspSubmissionUncertain'), { variant: 'warning' });
      closeModal();
    },
    onError: (error) => {
      if (isFinpspSubmissionUncertain(error)) {
        void invalidate(FINPSP_PAYMENT_QUERY_KEYS);
        enqueueSnackbar(t('finpspSubmissionUncertain'), { variant: 'warning' });
        closeModal();
        return;
      }

      handlePaymentError(error, { failureMessage });
    },
  });
};

export const useFinpspDeposit = () =>
  useFinpspCreation<FinpspDepositRequest>(
    API_ROUTES.FINPSP.DEPOSIT,
    PAYMENT_TRANSACTION_TYPE.DEPOSIT,
    'depositRequestFailed'
  );

export const useFinpspWithdrawal = () =>
  useFinpspCreation<FinpspWithdrawalRequest>(
    API_ROUTES.FINPSP.WITHDRAW,
    PAYMENT_TRANSACTION_TYPE.WITHDRAW,
    'withdrawRequestFailed'
  );
